// @flow
import AdditionalImageLoader from './AdditionalImageLoader'
import MediaLoaderContainer from './MediaLoaderContainer'
import PreviewImageCropperContainer from './PreviewImageCropperContainer'
import React, { Component, Fragment } from 'react'
import styled, { type StyledComponent } from 'styled-components'
import type { Configs, Crop, ImageSizeRestrictions, ImageConfig } from '../types'
import {
  getImageSizeRestrictionsFromSingleConfig,
  Media,
  CropWrapper,
  Row,
  type ControlVisibility
} from '../AdEdit'

const MediaHeader = styled.div`
  color: #666;
  padding: 10px 0 5px 0;
  font-size: 16px;
  font-weight: bold;
`
const Image: StyledComponent<Object, any, any> = styled.div`
  padding-right: 5px;
`
const MediaSubheader = styled.div`
  color: #666;
  font-size: 12px;
`
const ImageVariations: StyledComponent<Object, any, any> = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-top: 2px;
`
const RemoveMediaLink: StyledComponent<Object, any, any> = styled.a`
  font-size: 12px;
  display: inline-block;
  margin-bottom: 12px;
`

type Errors = Array<string>

export type Props = {|
  action: 'copy' | 'update' | 'create',
  allowMultiUpload: boolean,
  columnImageErrors: Errors,
  columnImageSizeRestrictions: ImageSizeRestrictions,
  columnImageStoreId: ?string,
  configs: Configs,
  controlVisibility: ControlVisibility,
  fbImageCrop: ?Crop,
  fbInstagramImageCrop: ?Crop,
  fbInstagramImageErrors: Errors,
  fbInstagramImageStoreId: ?string,
  fbStoriesImageCrop: ?Crop,
  fbStoriesImageErrors: Errors,
  fbStoriesImageStoreId: ?string,
  fbThumbnailImageCrop: ?Crop,
  fbThumbnailImageErrors: Errors,
  fbThumbnailImageStoreId: ?string,
  glImageCrop: ?Crop,
  isReplica: boolean,
  multiImageErrors: Errors,
  multiImageSizeRestrictions: ImageSizeRestrictions,
  multiImageStoreId: ?string,
  onColumnImageChange: (Array<string>) => void,
  onFbImageCropChange: (Crop) => void,
  onFbInstagramImageChange: (string) => void,
  onFbInstagramImageCropChange: (Crop) => void,
  onFbStoriesImageChange: (string) => void,
  onFbStoriesImageCropChange: (Crop) => void,
  onFbThumbnailImageChange: (string) => void,
  onFbThumbnailImageCropChange: (Crop) => void,
  onGlImageCropChange: (Crop) => void,
  onMediaRemove: () => void,
  onMultiImageChange: (Array<string>) => void,
  onTmColumnImageCropChange: (Crop) => void,
  onTmMultiImageCropChange: (Crop) => void,
  onTmSmallImageChange: (string) => void,
  onTmSmallImageCropChange: (Crop) => void,
  onVideoChange: (string) => void,
  onVkArticleImageCropChange: (Crop) => void,
  onVkBigImageChange: (Array<string>) => void,
  onVkColumnImageCropChange: (Crop) => void,
  onVkMultiImageCropChange: (Crop) => void,
  onVkSiteImageCropChange: (Crop) => void,
  replica: any, // eslint-disable-line react/no-unused-prop-types
  replicaIndex: number, // eslint-disable-line react/no-unused-prop-types
  tmColumnImageCrop: ?Crop,
  tmMultiImageCrop: ?Crop,
  tmSmallImageCrop: ?Crop,
  tmSmallImageErrors: Errors,
  tmSmallImageStoreId: ?string,
  videoErrors: Errors,
  videoStoreId: ?string,
  vkArticleImageCrop: ?Crop,
  vkBigImageErrors: Errors,
  vkBigImageSizeRestrictions: ImageSizeRestrictions,
  vkBigImageStoreId: ?string,
  vkColumnImageCrop: ?Crop,
  vkMultiImageCrop: ?Crop,
  vkSiteImageCrop: ?Crop,
  vkMultiImageConfig: ImageConfig,
  tmMultiImageConfig: ImageConfig,
  fbMultiImageConfig: ImageConfig,
  glMultiImageConfig: ImageConfig
|}

const t = {
  big_image: 'Произвольная картинка',
  choose_for_instagram: 'Заменить для Instagram',
  choose_for_stories: 'Заменить для Stories',
  column_image: 'Колонка',
  fb_crop_title: 'Facebook',
  fb_instagram_crop: 'Instagram',
  fb_stories_crop: 'Stories',
  fb_thumbnail_image: 'Обложка видео в Facebook',
  gl_crop_title: 'Google',
  main_image: 'Основная картинка',
  multi_image: 'Мультиформат',
  remove_media: 'Удалить',
  tm_crop_title: 'myTarget',
  tm_small_crop: 'myTarget доп.',
  video: 'Видео',
  video_vk_subheader: '(будет загружено в альбом Вашей группы ВКонтакте)',
  vk_crop_title: 'ВКонтакте',
  vk_without_button_crop: 'ВКонтакте без кнопки'
}

export default class ReplicatedData extends Component<Props> {
  render() {
    const visibility = this.props.controlVisibility

    return (
      <Fragment>
        {visibility.video && (
          <Row>
            {!this.props.isReplica && <MediaHeader>{t.video}</MediaHeader>}
            {!this.props.isReplica && visibility.vk && (
              <MediaSubheader>{t.video_vk_subheader}</MediaSubheader>
            )}
            <Media>
              <Image className="t-Video">
                <MediaLoaderContainer
                  storeId={this.props.videoStoreId}
                  errors={this.props.videoErrors}
                  height={150}
                  onLoad={(storeIds) => this.props.onVideoChange(storeIds[0])}
                  mediaType="video"
                />
              </Image>
            </Media>
            {visibility.fbThumbnailImage && (
              <Row>
                <MediaHeader>{t.fb_thumbnail_image}</MediaHeader>
                <Media>
                  <Image className="t-FbThumbnailImage">
                    <MediaLoaderContainer
                      storeId={this.props.fbThumbnailImageStoreId}
                      imageSizeRestrictions={this.props.multiImageSizeRestrictions}
                      errors={this.props.fbThumbnailImageErrors}
                      height={150}
                      onLoad={(storeIds) => this.props.onFbThumbnailImageChange(storeIds[0])}
                      mediaType="image"
                    />
                    {this.props.fbThumbnailImageStoreId && (
                      <ImageVariations>
                        <CropWrapper>
                          <PreviewImageCropperContainer
                            title={t.fb_crop_title}
                            imageStoreId={this.props.fbThumbnailImageStoreId}
                            config={this.props.configs.imageConfigCollection.promo.facebook}
                            crop={this.props.fbThumbnailImageCrop}
                            onChange={this.props.onFbThumbnailImageCropChange}
                          />
                        </CropWrapper>
                      </ImageVariations>
                    )}
                  </Image>
                </Media>
              </Row>
            )}
          </Row>
        )}
        {visibility.multiImage && (
          <Row>
            {!this.props.isReplica && (
              <MediaHeader>{visibility.columnImage ? t.multi_image : t.main_image}</MediaHeader>
            )}
            <Media>
              <Image>
                <div className="t-MultiImage">
                  <MediaLoaderContainer
                    storeId={this.props.multiImageStoreId}
                    imageSizeRestrictions={this.props.multiImageSizeRestrictions}
                    errors={this.props.multiImageErrors}
                    height={150}
                    onLoad={this.props.onMultiImageChange}
                    mediaType="image"
                    multiple={this.props.allowMultiUpload}
                  />
                </div>
                {visibility.fbInstagramImage &&
                  this.props.multiImageStoreId &&
                  !this.props.fbInstagramImageStoreId && (
                    <div className="t-FbInstagramImage">
                      <AdditionalImageLoader
                        onLoad={this.props.onFbInstagramImageChange}
                        titleParams={{ title: t.choose_for_instagram }}
                        storeId={this.props.fbInstagramImageStoreId}
                        errors={this.props.fbInstagramImageErrors}
                        config={this.props.configs.instagramImageConfigs}
                      />
                    </div>
                  )}
                {visibility.fbStoriesImage &&
                  this.props.multiImageStoreId &&
                  !this.props.fbStoriesImageStoreId && (
                    <div className="t-FbStoriesImage">
                      <AdditionalImageLoader
                        onLoad={this.props.onFbStoriesImageChange}
                        titleParams={{ title: t.choose_for_stories }}
                        storeId={this.props.fbStoriesImageStoreId}
                        errors={this.props.fbStoriesImageErrors}
                        config={this.props.configs.storiesImageConfigs}
                      />
                    </div>
                  )}
              </Image>
              {this.props.multiImageStoreId && (
                <ImageVariations>
                  {visibility.vkMultiCrop && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.vk_crop_title}
                        imageStoreId={this.props.multiImageStoreId}
                        config={this.props.vkMultiImageConfig}
                        crop={this.props.vkMultiImageCrop}
                        onChange={this.props.onVkMultiImageCropChange}
                      />
                    </Image>
                  )}
                  {visibility.vkArticleCrop && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.vk_without_button_crop}
                        imageStoreId={this.props.multiImageStoreId}
                        config={this.props.configs.imageConfigCollection.promo.vk}
                        crop={this.props.vkArticleImageCrop}
                        onChange={this.props.onVkArticleImageCropChange}
                      />
                    </Image>
                  )}
                  {visibility.vkSiteCrop && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.vk_crop_title}
                        imageStoreId={this.props.multiImageStoreId}
                        config={this.props.configs.imageConfigCollection.site.vk}
                        crop={this.props.vkSiteImageCrop}
                        onChange={this.props.onVkSiteImageCropChange}
                      />
                    </Image>
                  )}
                  {visibility.tm && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.tm_crop_title}
                        imageStoreId={this.props.multiImageStoreId}
                        config={this.props.tmMultiImageConfig}
                        crop={this.props.tmMultiImageCrop}
                        onChange={this.props.onTmMultiImageCropChange}
                      />
                    </Image>
                  )}
                  {visibility.tmSmallImage && this.props.tmSmallImageStoreId && (
                    <CropWrapper>
                      <Image>
                        <PreviewImageCropperContainer
                          title={t.tm_small_crop}
                          imageStoreId={this.props.tmSmallImageStoreId}
                          config={this.props.configs.tmSmallImageConfigs}
                          crop={this.props.tmSmallImageCrop}
                          onChange={this.props.onTmSmallImageCropChange}
                        />
                      </Image>
                      <Image className="t-TmSmallImage">
                        <MediaLoaderContainer
                          storeId={this.props.tmSmallImageStoreId}
                          imageSizeRestrictions={getImageSizeRestrictionsFromSingleConfig(
                            this.props.configs.tmSmallImageConfigs
                          )}
                          errors={this.props.tmSmallImageErrors}
                          onLoad={(storeIds) => this.props.onTmSmallImageChange(storeIds[0])}
                          mediaType="image"
                          withoutMediaPreview
                        />
                      </Image>
                    </CropWrapper>
                  )}
                  {visibility.fbMultiCrop && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.fb_crop_title}
                        imageStoreId={this.props.multiImageStoreId}
                        config={this.props.fbMultiImageConfig}
                        crop={this.props.fbImageCrop}
                        onChange={this.props.onFbImageCropChange}
                      />
                    </Image>
                  )}
                  {visibility.fbInstagramImage && this.props.fbInstagramImageStoreId && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.fb_instagram_crop}
                        imageStoreId={this.props.fbInstagramImageStoreId}
                        config={this.props.configs.instagramImageConfigs}
                        crop={this.props.fbInstagramImageCrop}
                        onChange={this.props.onFbInstagramImageCropChange}
                      />
                      <div className="t-FbInstagramImage">
                        <AdditionalImageLoader
                          onLoad={this.props.onFbInstagramImageChange}
                          storeId={this.props.fbInstagramImageStoreId}
                          errors={this.props.fbInstagramImageErrors}
                          config={this.props.configs.instagramImageConfigs}
                        />
                      </div>
                    </Image>
                  )}
                  {visibility.fbStoriesImage && this.props.fbStoriesImageStoreId && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.fb_stories_crop}
                        imageStoreId={this.props.fbStoriesImageStoreId}
                        config={this.props.configs.storiesImageConfigs}
                        crop={this.props.fbStoriesImageCrop}
                        height={108}
                        onChange={this.props.onFbStoriesImageCropChange}
                      />
                      <div className="t-FbStoriesImage">
                        <AdditionalImageLoader
                          onLoad={this.props.onFbStoriesImageChange}
                          storeId={this.props.fbStoriesImageStoreId}
                          errors={this.props.fbStoriesImageErrors}
                          config={this.props.configs.storiesImageConfigs}
                        />
                      </div>
                    </Image>
                  )}
                  {visibility.glMultiCrop && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.gl_crop_title}
                        imageStoreId={this.props.multiImageStoreId}
                        config={this.props.glMultiImageConfig}
                        crop={this.props.glImageCrop}
                        onChange={this.props.onGlImageCropChange}
                      />
                    </Image>
                  )}
                </ImageVariations>
              )}
            </Media>
          </Row>
        )}
        {visibility.columnImage && (
          <Row>
            {!this.props.isReplica && <MediaHeader>{t.column_image}</MediaHeader>}
            <Media>
              <Image className="t-ColumnImage">
                <MediaLoaderContainer
                  storeId={this.props.columnImageStoreId}
                  imageSizeRestrictions={this.props.columnImageSizeRestrictions}
                  errors={this.props.columnImageErrors}
                  height={150}
                  onLoad={this.props.onColumnImageChange}
                  mediaType="image"
                  multiple={this.props.allowMultiUpload}
                />
              </Image>
              {this.props.columnImageStoreId && (
                <ImageVariations>
                  {visibility.vkColumnCrop && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.vk_crop_title}
                        imageStoreId={this.props.columnImageStoreId}
                        config={this.props.configs.imageConfigCollection.regular.vk}
                        crop={this.props.vkColumnImageCrop}
                        onChange={this.props.onVkColumnImageCropChange}
                      />
                    </Image>
                  )}
                  {visibility.tmColumnCrop && (
                    <Image>
                      <PreviewImageCropperContainer
                        title={t.tm_crop_title}
                        imageStoreId={this.props.columnImageStoreId}
                        config={this.props.configs.imageConfigCollection.regular.target_mail}
                        crop={this.props.tmColumnImageCrop}
                        onChange={this.props.onTmColumnImageCropChange}
                      />
                    </Image>
                  )}
                </ImageVariations>
              )}
            </Media>
          </Row>
        )}
        {visibility.vkBigImage && (
          <Row>
            {!this.props.isReplica && <MediaHeader>{t.big_image}</MediaHeader>}
            <Media>
              <Image className="t-VkBigImage">
                <MediaLoaderContainer
                  storeId={this.props.vkBigImageStoreId}
                  imageSizeRestrictions={this.props.vkBigImageSizeRestrictions}
                  errors={this.props.vkBigImageErrors}
                  height={150}
                  onLoad={this.props.onVkBigImageChange}
                  mediaType="image"
                  multiple={this.props.allowMultiUpload}
                  maxImageSize={12}
                />
              </Image>
            </Media>
          </Row>
        )}
        {this.props.action !== 'update' && (
          <RemoveMediaLink
            href="#"
            onClick={(e) => {
              e.preventDefault()
              this.props.onMediaRemove()
            }}
          >
            {t.remove_media}
          </RemoveMediaLink>
        )}
      </Fragment>
    )
  }
}
